import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Scroll container
   * @type {EventTarget | null}
   */
  #scrollContainer = null;

  /**
   * Whether the header has been scrolled offscreen
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * Timeout for hiding animation
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global --header-height custom property up to date.
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);

    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when it is sticky.
   *
   * IMPORTANT:
   * We force alwaysSticky = true so the header remains active
   * instead of switching between hidden/visible states.
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: 1,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      // Always use the stable sticky state.
      this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';

      if (this.dataset.themeColor) {
        changeMetaThemeColor(this.dataset.themeColor);
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu.
   *
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer.
   *
   * @param {boolean} hideMenu
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;

      const overflowList = this.querySelector('overflow-list');

      if (overflowList) {
        overflowList.removeAttribute('minimum-reached');
      }
    }

    setHeaderMenuStyle();
  }

  /**
   * Rebinds scroll listener and IntersectionObserver when viewport changes.
   *
   * We always use the sticky header behavior.
   */
  #handleBreakpointChange = () => {
    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);

      this.#scrollContainer = getScrollEventTarget();

      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
    }

    // Recreate IntersectionObserver with the new root
    this.#intersectionObserver?.disconnect();

    this.#intersectionObserver = null;

    this.#observeStickyPosition(true);
  };

  /**
   * Handles window scrolling using requestAnimationFrame.
   */
  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;

      this.#updateScrollState();
    });
  };

  /**
   * Updates sticky header scroll state.
   *
   * IMPORTANT:
   * The original Atelier code used scroll-up behavior where the
   * header could switch between active/idle states.
   *
   * This version keeps the header sticky and visible.
   */
  #updateScrollState = () => {
    const scrollTop = getScrollTop();

    const headerTop = this.getBoundingClientRect().top;

    const isScrollingUp = scrollTop < this.#lastScrollTop;

    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    /*
     * ALWAYS-STICKY MODE
     *
     * The header never enters the "idle/hidden" state.
     * It remains active once the page is scrolled.
     */
    if (isAtTop) {
      this.#offscreen = false;

      this.dataset.stickyState = 'inactive';
      this.dataset.scrollDirection = 'none';
    } else {
      this.#offscreen = false;

      this.dataset.stickyState = 'active';

      /*
       * Keep scroll direction information for the theme,
       * but never use it to hide the header.
       */
      if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();

    this.#resizeObserver.observe(this);

    this.addEventListener(
      'overflowMinimum',
      this.#handleOverflowMinimum
    );

    /*
     * FORCE ALWAYS-STICKY HEADER
     *
     * We intentionally do not use:
     * this.getAttribute('sticky')
     *
     * because the original theme can be configured as
     * "scroll-up", which causes the flickering behavior.
     */
    const stickyMode = 'always';

    this.#observeStickyPosition(true);

    this.#scrollContainer = getScrollEventTarget();

    this.#scrollContainer.addEventListener(
      'scroll',
      this.#handleWindowScroll
    );

    scrollContainerMediaQuery.addEventListener(
      'change',
      this.#handleBreakpointChange
    );

    // Initialize current scroll state
    this.#updateScrollState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver.disconnect();

    this.#intersectionObserver?.disconnect();

    this.removeEventListener(
      'overflowMinimum',
      this.#handleOverflowMinimum
    );

    scrollContainerMediaQuery.removeEventListener(
      'change',
      this.#handleBreakpointChange
    );

    this.#scrollContainer?.removeEventListener(
      'scroll',
      this.#handleWindowScroll
    );

    this.#scrollContainer = null;

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);

      this.#scrollRafId = null;
    }

    document.body.style.setProperty(
      '--header-height',
      '0px'
    );
  }
}

if (!customElements.get('header-component')) {
  customElements.define(
    'header-component',
    HeaderComponent
  );
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');

  const headerGroup = document.querySelector('#header-group');

  /*
   * Initial header heights are set via inline script
   * in theme.liquid.
   *
   * ResizeObserver handles dynamic updates after page load.
   */

  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce(
        (totalHeight, entry) => {
          if (
            entry.target !== header ||
            (
              header?.hasAttribute('transparent') &&
              header?.parentElement?.nextElementSibling
            )
          ) {
            return (
              totalHeight +
              (entry.borderBoxSize[0]?.blockSize ?? 0)
            );
          }

          return totalHeight;
        },
        0
      );

      const roundedHeaderGroupHeight =
        Math.round(headerGroupHeight);

      document.body.style.setProperty(
        '--header-group-height',
        `${roundedHeaderGroupHeight}px`
      );
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    /*
     * Observe all children of the header group.
     */
    const children = headerGroup.children;

    for (let i = 0; i < children.length; i++) {
      const element = children[i];

      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    /*
     * Observe the header group for child changes.
     */
    const mutationObserver = new MutationObserver(
      (mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'childList') {
            const children = headerGroup.children;

            for (let i = 0; i < children.length; i++) {
              const element = children[i];

              if (element instanceof HTMLElement) {
                resizeObserver.observe(element);
              }
            }
          }
        }
      }
    );

    mutationObserver.observe(
      headerGroup,
      {
        childList: true,
      }
    );
  }
});